import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from "react-redux";
import Grid from "@material-ui/core/Grid";
import {useSnackbar} from "notistack";
import {connect, transceive, autoConnect} from "./actions";
import {byteToHexString, hexStringToByte} from "./util";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormGroup from "@material-ui/core/FormGroup";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import StarIcon from "@material-ui/icons/Star";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import AvTimerIcon from "@material-ui/icons/AvTimer";
import CropFreeIcon from '@material-ui/icons/CropFree';
import PublishIcon from '@material-ui/icons/Publish';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import * as base32 from "hi-base32";
import pbkdf2Hmac from "pbkdf2-hmac";
import jsSHA from "jssha";
import jsQR from 'jsqr';
import {saveAs} from 'file-saver';
import {secureGetItem} from './secureStorage';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    margin: theme.spacing(2), // 移动端: 16px
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
      margin: theme.spacing(3), // 平板: 24px
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
    },
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  advancedRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  formControl: {
    minWidth: 160,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  warningText: {
    color: theme.palette.warning.main,
    marginTop: theme.spacing(2),
  },
  keyPreview: {
    fontFamily: 'monospace',
    background: theme.palette.action.hover,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    wordBreak: 'break-all',
    marginTop: theme.spacing(1),
  },
  qrVideo: {
    width: '100%',
    maxHeight: 300,
    background: 'black',
    borderRadius: theme.shape.borderRadius,
  },
  hiddenInput: {
    display: 'none',
  },
  tableContainer: {
    [theme.breakpoints.down('sm')]: {
      overflowX: 'auto',
      '& table': {
        minWidth: 650,
      },
    },
  },
  credentialsActions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  tableActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  dialogActions: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      '& > button': {
        width: '100%',
        marginLeft: '0 !important',
        marginTop: theme.spacing(1),
      },
    },
  },
  hexPreview: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    marginTop: theme.spacing(1),
  },
  totpCode: {
    fontFamily: 'monospace',
    fontSize: '1.6rem',
    letterSpacing: '0.2rem',
    marginTop: theme.spacing(1),
  },
  exportTextarea: {
    fontFamily: 'monospace',
  },
}));

/* eslint-disable no-throw-literal */
function parseTLV(tlv) {
  let res = [];
  for (let i = 0; i < tlv.length;) {
    if (i + 1 >= tlv.length) {
      throw 'Bad TLV';
    }
    let tag = tlv[i];
    let len = tlv[i + 1];
    // 修复：i(tag) + 1(len) + len(value) 的总长度不应超过 tlv.length
    // 正确的判断：i + 2 + len > tlv.length
    if (i + 2 + len > tlv.length) {
      throw 'Bad TLV Length'
    }
    let value = tlv.slice(i + 2, i + 2 + len);
    i += len + 2;
    res.push({
      tag,
      len,
      value
    })
  }
  return res;
}

function isOldProtocol(firmwareVersion) {
  // 如果 firmwareVersion 为空，默认使用新协议（避免误判为旧协议）
  if (!firmwareVersion || firmwareVersion === '') {
    return false;
  }
  return firmwareVersion < "1.5";
}

function buildKeyArrayFromSecret(secret, encoding) {
  const normalizedEncoding = encoding || 'Base32';
  if (!secret || !secret.trim()) {
    throw new Error('Secret cannot be empty');
  }
  if (normalizedEncoding === 'UTF-8') {
    return new TextEncoder().encode(secret);
  }
  if (normalizedEncoding === 'HEX') {
    const cleaned = secret.replace(/\s+/g, '').toLowerCase();
    if (cleaned.length % 2 !== 0) {
      throw new Error('Hex secret length must be even');
    }
    if (!/^[0-9a-f]+$/.test(cleaned)) {
      throw new Error('Hex secret contains invalid characters');
    }
    const bytes = new Uint8Array(cleaned.length / 2);
    for (let i = 0; i < cleaned.length; i += 2) {
      bytes[i / 2] = parseInt(cleaned.substr(i, 2), 16);
    }
    return bytes;
  }
  if (normalizedEncoding === 'Base32') {
    const cleaned = secret.replace(/\s+/g, '').toUpperCase();
    try {
      const decoded = base32.decode.asBytes(cleaned);
      if (!decoded || decoded.length === 0) {
        throw new Error('Invalid Base32 secret');
      }
      return Uint8Array.from(decoded);
    } catch (err) {
      throw new Error('Invalid Base32 secret');
    }
  }
  throw new Error(`Unsupported key encoding: ${encoding}`);
}

/**
 * 🔒 安全增强：解析 otpauth URI，包含完整的输入验证
 * @param {string} uri - otpauth URI
 * @returns {Object} 解析结果
 */
function parseOtpauthUri(uri) {
  // 验证 1: 空值检查
  if (!uri || !uri.trim()) {
    throw new Error('Empty otpauth URI');
  }
  
  // 🔒 验证 2: 长度限制（防止 DoS）
  const MAX_URI_LENGTH = 2000;
  if (uri.length > MAX_URI_LENGTH) {
    throw new Error(`URI too long (max ${MAX_URI_LENGTH} characters)`);
  }
  
  // 验证 3: URL 格式
  let url;
  try {
    url = new URL(uri.trim());
  } catch (err) {
    throw new Error('Invalid otpauth URI');
  }
  
  // 验证 4: 协议必须是 otpauth
  if (url.protocol !== 'otpauth:') {
    throw new Error('URI scheme must be otpauth');
  }
  
  // 验证 5: 类型检查
  const typeRaw = url.host.toUpperCase();
  if (typeRaw !== 'HOTP' && typeRaw !== 'TOTP') {
    throw new Error('Type must be HOTP or TOTP');
  }
  const type = typeRaw;
  
  // 🔒 验证 6: name 清理和长度限制
  const labelRaw = url.pathname.replace(/^\//, '');
  let name = decodeURIComponent(labelRaw || '');
  name = name.replace(/[<>"'&]/g, '').substring(0, 100).trim(); // 移除 XSS 字符
  if (!name) {
    throw new Error('Name is required');
  }
  
  // 如果 name 包含 "issuer:account" 格式，提取 issuer 前缀
  let extractedIssuer = null;
  if (name.includes(':')) {
    const colonIndex = name.indexOf(':');
    extractedIssuer = name.substring(0, colonIndex).trim();
    // 保留完整的 name (包含 issuer 前缀)
    // 注意：我们不修改 name，保持完整格式
  }
  
  // 🔒 验证 7: secret 格式和长度检查
  const secret = url.searchParams.get('secret');
  if (!secret) {
    throw new Error('otpauth URI missing secret');
  }
  // Base32 只能包含 A-Z, 2-7, 和可选的 =
  if (!/^[A-Z2-7]+=*$/i.test(secret)) {
    throw new Error('Invalid Base32 secret format');
  }
  if (secret.length > 200) {
    throw new Error('Secret too long (max 200 characters)');
  }
  
  // 验证 8: 算法白名单
  const algoParam = (url.searchParams.get('algorithm') || 'SHA1').toUpperCase();
  const ALLOWED_ALGOS = ['SHA1', 'SHA256', 'SHA512'];
  if (!ALLOWED_ALGOS.includes(algoParam)) {
    throw new Error(`Unsupported algorithm: ${algoParam}`);
  }
  const algo = algoParam === 'SHA256' ? 'HMAC-SHA256' : 
               algoParam === 'SHA512' ? 'HMAC-SHA512' : 'HMAC-SHA1';
  
  // 🔒 验证 9: digits 范围检查
  const digits = parseInt(url.searchParams.get('digits') || '6', 10);
  if (digits < 6 || digits > 8) {
    throw new Error('Digits must be 6, 7, or 8');
  }
  
  // 🔒 验证 10: period 范围检查
  const period = parseInt(url.searchParams.get('period') || '30', 10);
  if (period < 15 || period > 300) {
    throw new Error('Period must be between 15 and 300 seconds');
  }
  
  // 验证 11: counter 范围检查
  const counter = parseInt(url.searchParams.get('counter') || '0', 10);
  if (counter < 0 || counter > 0xFFFFFFFF) {
    throw new Error('Counter out of range');
  }
  
  // 🔒 验证 12: issuer 清理
  const issuerParam = url.searchParams.get('issuer');
  let issuer = issuerParam ? decodeURIComponent(issuerParam) : extractedIssuer;
  if (issuer) {
    issuer = issuer.replace(/[<>"'&]/g, '').substring(0, 100).trim();
  }
  
  const requireTouch = url.searchParams.get('requireTouch') === '1';

  const result = {
    name,  // 使用完整的 name (包含 issuer 前缀，如 "Tencent Cloud Services:账号ID：100000786592")
    secret,
    encoding: 'Base32',
    type,
    algo,
    digits: Number.isFinite(digits) ? digits : 6,
    period: Number.isFinite(period) ? period : 30,
    counter: Number.isFinite(counter) ? counter : 0,
    issuer,  // issuer 来自 URL 参数或从 name 中提取
    requireTouch,
    onlyIncreasing: type === 'HOTP',
  };
  
  console.log('parseOtpauthUri result:', result);
  return result;
}


const CREDENTIAL_STORE_KEY = 'canokey-webconsole-oath-store-v2';

function encodeBase32(bytes) {
  if (!bytes || bytes.length === 0) {
    return '';
  }
  return base32.encode(Array.from(bytes)).replace(/=+$/g, '').replace(/\s+/g, '');
}


function normalizeImportedCredential(raw) {
  if (!raw || !raw.name) {
    throw new Error('Missing name in credential record');
  }
  if (!raw.secret) {
    throw new Error(`Credential ${raw.name} missing secret`);
  }
  const encoding = raw.encoding || 'Base32';
  const bytes = buildKeyArrayFromSecret(raw.secret, encoding);
  const type = raw.type === 'HOTP' ? 'HOTP' : 'TOTP';
  const algo = raw.algo === 'HMAC-SHA256' ? 'HMAC-SHA256' : 'HMAC-SHA1';
  const digits = Number.parseInt(raw.digits, 10);
  const period = Number.parseInt(raw.period, 10);
  const counter = Number.parseInt(raw.counter, 10);
  return {
    name: raw.name.trim(),
    secret: encodeBase32(bytes),
    type,
    algo,
    digits: Number.isNaN(digits) ? 6 : digits,
    period: Number.isNaN(period) ? 30 : period,
    counter: Number.isNaN(counter) ? 0 : counter,
    issuer: raw.issuer || '',
    requireTouch: !!raw.requireTouch,
    onlyIncreasing: raw.onlyIncreasing !== undefined ? !!raw.onlyIncreasing : type === 'HOTP',
  };
}

export default function Oath() {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const device = useSelector(state => state.device);
  const firmwareVersion = useSelector(state => state.firmwareVersion);
  const adminAuthenticated = useSelector(state => state.adminAuthenticated);
  const dispatch = useDispatch();
  const {enqueueSnackbar} = useSnackbar();
  const [entries, setEntries] = useState([]);
  const [passphraseDialogOpen, setPassphraseDialogOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [authenticated, setAuthenticated] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const salt = useRef('');
  const challenge = useRef('');
  const isLoading = useRef(false);
  const adminAuthenticatedRef = useRef(adminAuthenticated);

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const videoRef = useRef(null);
  
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameOldName, setRenameOldName] = useState('');
  const [renameNewName, setRenameNewName] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetName, setDeleteTargetName] = useState('');
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const qrScanIntervalRef = useRef(null);
  const [qrError, setQrError] = useState('');
  const [qrActive, setQrActive] = useState(false);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState('');
  const [importError, setImportError] = useState('');

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportData, setExportData] = useState('');
  const [exportError, setExportError] = useState('');
  const fileInputRef = useRef(null);

  // add
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyEncoding, setKeyEncoding] = useState('UTF-8');
  const [algo, setAlgo] = useState('HMAC-SHA1');
  const [type, setType] = useState('HOTP');
  const [onlyIncreasing, setOnlyIncreasing] = useState(false);
  const [requireTouch, setRequireTouch] = useState(false);
  const [digits, setDigits] = useState(6);
  const [period, setPeriod] = useState(30);
  const [counter, setCounter] = useState(0);
  // 🔒 安全增强：使用加密存储
  const [credentialStore] = useState(() => secureGetItem(CREDENTIAL_STORE_KEY, {}));
  
  // 二维码图片上传相关状态
  const qrImageInputRef = useRef(null);
  const [qrImageError, setQrImageError] = useState('');

  const resetAddForm = useCallback(() => {
    setName('');
    setKey('');
    setKeyEncoding('UTF-8');
    setAlgo('HMAC-SHA1');
    setType('HOTP');
    setOnlyIncreasing(false);
    setRequireTouch(false);
    setDigits(6);
    setPeriod(30);
    setCounter(0);
    setQrImageError('');
  }, []);

  useEffect(() => {
    if (type === 'TOTP' && onlyIncreasing) {
      setOnlyIncreasing(false);
    }
  }, [type, onlyIncreasing]);

  // 同步 adminAuthenticated 到 ref
  useEffect(() => {
    adminAuthenticatedRef.current = adminAuthenticated;
  }, [adminAuthenticated]);

  const doList = useCallback(async () => {
    // 使用组件级别的 firmwareVersion，避免依赖问题
    // 修复: 旧协议也应该使用 0xA1 (LIST) 命令，而不是 0x03 (SET_CODE)
    let cmd = '00A1000000';
    let res = await dispatch(transceive(cmd, false, true));
    console.log('doList: Command:', cmd, 'Response:', res);
    if (!res.endsWith("9000")) {
      throw 'Failed to list OATH credentials';
    }
    
    let responseData = res.substring(0, res.length - 4);
    console.log('doList: Response data length:', responseData.length);
    
    if (responseData.length === 0) {
      console.log('doList: No credentials, setting entries to empty array');
      setEntries([]);
      return;
    }
    
    let tlv = parseTLV(hexStringToByte(responseData));
    let entries = [];
    if (isOldProtocol(firmwareVersion)) {
      if (tlv.length % 2 === 1) {
        throw 'Invalid length of TLV';
      }
      for (let i = 0; i < tlv.length; i += 2) {
        if (tlv[i].tag !== 0x71 || tlv[i + 1].tag !== 0x75) {
          throw 'Bad tag in tlv'
        }

        let rawType = tlv[i + 1].value[0] >> 4;
        let type = 'unknown';
        if (rawType === 0x1) {
          type = 'HOTP';
        } else if (rawType === 0x2) {
          type = 'TOTP';
        }

        let rawAlgo = tlv[i + 1].value[0] & 0xF;
        let algo = 'unknown';
        if (rawAlgo === 0x1) {
          algo = 'HMAC-SHA1';
        } else if (rawAlgo === 0x2) {
          algo = 'HMAC-SHA256';
        }

        entries.push({
          name: new TextDecoder("utf-8").decode(tlv[i].value),
          type,
          algo,
        })
      }
    } else {
      for (let i = 0; i < tlv.length; ++i) {
        if (tlv[i].tag !== 0x72) {
          throw 'Bad tag in tlv'
        }

        let rawType = tlv[i].value[0] >> 4;
        let type = 'unknown';
        if (rawType === 0x1) {
          type = 'HOTP';
        } else if (rawType === 0x2) {
          type = 'TOTP';
        }

        let rawAlgo = tlv[i].value[0] & 0xF;
        let algo = 'unknown';
        if (rawAlgo === 0x1) {
          algo = 'HMAC-SHA1';
        } else if (rawAlgo === 0x2) {
          algo = 'HMAC-SHA256';
        }

        entries.push({
          name: new TextDecoder("utf-8").decode(tlv[i].value.slice(1)),
          type,
          algo,
        })
      }
    }
    setEntries(entries);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // 移除 firmwareVersion 依赖，避免状态变化导致的重复执行

  const doAuthenticate = useCallback(async () => {
    const key = await pbkdf2Hmac(passphrase, hexStringToByte(salt.current), 1000, 16, 'SHA-1');
    const shaObj = new jsSHA("SHA-1", "HEX", {
      hmacKey: {value: key, format: "ARRAYBUFFER"},
    });
    shaObj.update(challenge.current);
    const response = shaObj.getHash("HEX");
    let res = await dispatch(transceive(`00A30000207514${response}7408${challenge.current}`));
    if (!res.endsWith("9000")) {
      throw 'Invalid passphrase';
    } else {
      setAuthenticated(true);
      setPassphraseDialogOpen(false);
    }
  }, [dispatch, setAuthenticated, passphrase]);

  const onKeyPress = useCallback(async (e) => {
    if (e.key === 'Enter') {
      await doAuthenticate();
    }
  }, [doAuthenticate]);

  const selectOathApplet = useCallback(async () => {
    try {
      if (device === null) {
        // Try auto-connect first (no dialog)
        const autoConnected = await dispatch(autoConnect());
        if (!autoConnected) {
          // If auto-connect fails, show device picker
          const connected = await dispatch(connect());
          if (!connected) {
            throw 'Cannot connect to GriffonOakey';
          }
        }
      }

      // 直接使用 Redux 中的 firmwareVersion，不在此处读取
      // 避免选择 Admin applet 导致切换 applet，可能触发认证检查
      let version = firmwareVersion;
      
      // 如果 firmwareVersion 为空，使用默认值（假设是新协议）
      // Overview 页面会在连接时自动读取真实版本
      if (version === '') {
        console.log('⚠️ firmwareVersion not loaded yet, assuming new protocol');
        version = '2.0'; // 默认使用新协议
      }

      let res = await dispatch(transceive("00A4040007A0000005272101"));
      console.log('OATH Select Response:', res, 'Length:', res.length);
      if (!res.endsWith("9000")) {
        const statusCode = res.length >= 4 ? res.slice(-4) : res;
        throw `Selecting oath applet failed. Status: ${statusCode}. Please ensure OATH applet is installed on your device.`;
      }
      if (!isOldProtocol(version)) { // check if there is a password
        let tlv = parseTLV(hexStringToByte(res.substring(0, res.length - 4)));
        
        // 查找 challenge (tag 0x74，不是 0x71！)
        // 0x71 是凭证名称，0x74 是 challenge
        // 0x75 是 algorithm，0x76 是 version
        // 0x7A 是 salt
        let challengeTlv = tlv.find(t => t.tag === 0x74);
        let saltTlv = tlv.find(t => t.tag === 0x7A);
        
        if (challengeTlv) {
          // 有 challenge 说明需要密码认证
          console.log('OATH password protection detected');
          challenge.current = byteToHexString(challengeTlv.value);
          salt.current = saltTlv ? byteToHexString(saltTlv.value) : '';
          // 不在这里打开对话框，让 fetchEntries 处理
        } else {
          // 没有 challenge，说明没有密码保护
          console.log('OATH no password protection');
          setAuthenticated(true);
        }
      } else {
        setAuthenticated(true);
      }
    } catch (err) {
      console.error('selectOathApplet error:', err);
      throw err;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, dispatch]); // 移除 firmwareVersion 依赖，避免状态变化导致的重复执行

  // 统一认证机制：尝试直接LIST，如果Admin已认证则无需OATH密码
  const fetchEntries = useCallback(async () => {
    // Prevent concurrent calls
    if (isLoading.current) {
      console.log('fetchEntries: Already loading, skipping...');
      return;
    }
    isLoading.current = true;
    
    try {
      setAuthenticated(false); // Reset state
      await selectOathApplet();
      
      // 尝试直接LIST（无论OATH是否有密码）
      // 固件的统一认证机制：
      // - 如果Admin已认证，读操作可以通过
      // - 如果OATH有密码且未认证，返回 6982
      try {
        await doList();
        // LIST成功，标记为已认证
        setAuthenticated(true);
      } catch (listError) {
        // LIST失败，检查是否为认证错误
        const errorStr = listError.toString();
        if (errorStr.includes('6982')) {
          // 需要认证 - OATH applet 设置了密码，且Admin未认证
          console.log('⚠️ OATH requires authentication');
          console.log('💡 Tip: You can clear OATH password in Admin page for easier access');
          setPassphraseDialogOpen(true);
          // 不抛出错误，等待用户输入密码
        } else {
          // 其他错误
          throw listError;
        }
      }
    } catch (err) {
      console.error('fetchEntries error:', err);
      enqueueSnackbar(err.toString(), {variant: 'error'});
    } finally {
      isLoading.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar]); // 只依赖 enqueueSnackbar，避免函数依赖导致的重复执行

  const refresh = useCallback(async () => {
    try {
      if (device !== null) {
        await fetchEntries();
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]); // 只依赖 device，避免 fetchEntries 变化导致的重复执行

  // Removed unused handleCopyTotp function

  // 只在 device 和 adminAuthenticated 都就绪时刷新
  useEffect(() => {
    let isMounted = true;
    
    if (device && adminAuthenticated) {
      // 包装 refresh，检查组件是否仍然挂载
      (async () => {
        try {
          if (isMounted) {
            console.log('✅ Device connected and Admin authenticated, loading OATH entries');
            await refresh();
          }
        } catch (e) {
          if (isMounted) {
            console.error('Refresh error:', e);
          }
        }
      })();
    } else if (device && !adminAuthenticated) {
      console.log('⚠️ Device connected but Admin not authenticated, waiting...');
    }
    
    // 清理函数：标记组件已卸载
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, adminAuthenticated]); // 依赖 device 和 adminAuthenticated，不依赖 refresh

  const keyArray = useMemo(() => {
    try {
      if (!key) {
        return new Uint8Array();
      }
      return buildKeyArrayFromSecret(key, keyEncoding);
    } catch (err) {
      return new Uint8Array();
    }
  }, [key, keyEncoding]);

  // 处理二维码图片上传
  const handleQrImageUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      setQrImageError('Please upload an image file (PNG, JPG, GIF, WebP)');
      enqueueSnackbar('Please upload an image file', {variant: 'error'});
      return;
    }

    // 检查文件大小 (最大 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setQrImageError('Image file is too large. Please upload an image smaller than 10MB');
      enqueueSnackbar('Image file is too large', {variant: 'error'});
      return;
    }

    setQrImageError('');
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          // 创建 canvas 并绘制图片
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          // 获取图片数据
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          // 使用 jsQR 解析二维码
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code && code.data) {
            console.log('QR Code detected:', code.data);
            
            // 解析二维码内容
            try {
              const parsed = parseOtpauthUri(code.data);
              
              console.log('Auto-filling form with parsed data:', {
                name: parsed.name,
                secret: parsed.secret,
                issuer: parsed.issuer,
                type: parsed.type,
              });
              
              // 自动填充表单
              setName(parsed.name || '');
              setKey(parsed.secret || '');
              setKeyEncoding('Base32');  // otpauth URL 通常使用 Base32
              setType(parsed.type || 'TOTP');
              setAlgo(parsed.algo || 'HMAC-SHA1');
              setDigits(parsed.digits || 6);
              setPeriod(parsed.period || 30);
              setCounter(parsed.counter || 0);
              setRequireTouch(parsed.requireTouch || false);
              setOnlyIncreasing(parsed.onlyIncreasing || false);
              
              enqueueSnackbar('✅ QR code parsed successfully! Please verify the information', {variant: 'success'});
              setQrImageError('');
            } catch (parseError) {
              console.error('Failed to parse otpauth URL:', parseError);
              setQrImageError(`Failed to parse QR code: ${parseError.message}`);
              enqueueSnackbar(`Failed to parse QR code: ${parseError.message}`, {variant: 'error'});
            }
          } else {
            setQrImageError('No QR code detected. Please ensure the image is clear and contains a valid QR code');
            enqueueSnackbar('No QR code detected', {variant: 'warning'});
          }
        } catch (error) {
          console.error('QR code processing error:', error);
          setQrImageError(`Error processing image: ${error.message}`);
          enqueueSnackbar(`Failed to process image: ${error.message}`, {variant: 'error'});
        }
      };

      img.onerror = () => {
        setQrImageError('Failed to load image. Please check the file format');
        enqueueSnackbar('Failed to load image', {variant: 'error'});
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      setQrImageError('Failed to read file');
      enqueueSnackbar('Failed to read file', {variant: 'error'});
    };

    reader.readAsDataURL(file);

    // 清空 input，以便可以重复选择同一个文件
    event.target.value = '';
  }, [enqueueSnackbar]);


  const doAdd = useCallback(async () => {
    if (!name.trim()) {
      enqueueSnackbar('Name cannot be empty', {variant: 'error'});
      return;
    }
    if (!keyArray || keyArray.length === 0) {
      enqueueSnackbar('Secret cannot be empty', {variant: 'error'});
      return;
    }
    if (digits < 6 || digits > 8) {
      enqueueSnackbar('Digits must be between 6 and 8', {variant: 'error'});
      return;
    }
    if (type === 'TOTP' && (period < 15 || period > 300)) {
      enqueueSnackbar('Period must be between 15 and 300 seconds', {variant: 'error'});
      return;
    }
    try {
      await selectOathApplet();
      const payload = [];
      const nameBytes = new TextEncoder().encode(name);
      payload.push(0x71, nameBytes.length, ...nameBytes);

      const keyPayload = [];
      let flag = 0;
      if (type === 'HOTP') {
        flag |= 0x10;
      } else if (type === 'TOTP') {
        flag |= 0x20;
      } else {
        throw new Error('Invalid OATH type');
      }
      if (algo === 'HMAC-SHA1') {
        flag |= 0x01;
      } else if (algo === 'HMAC-SHA256') {
        flag |= 0x02;
      } else {
        throw new Error('Invalid algorithm');
      }
      keyPayload.push(flag);
      keyPayload.push(digits);
      if (type === 'HOTP') {
        const counterBuffer = new ArrayBuffer(8);
        const counterView = new DataView(counterBuffer);
        counterView.setBigUint64(0, BigInt(counter), false); // eslint-disable-line no-undef
        keyPayload.push(...new Uint8Array(counterBuffer));
      } else {
        // TOTP: period is 2 bytes (big-endian)
        keyPayload.push((period >> 8) & 0xFF, period & 0xFF);
      }
      keyPayload.push(...keyArray);
      payload.push(0x73, keyPayload.length, ...keyPayload);

      // TAG_PROPERTY (0x78) has NO length byte - just tag + value
      payload.push(0x78, (onlyIncreasing ? 0x01 : 0x00) | (requireTouch ? 0x02 : 0x00));

      // Note: Device does NOT support issuer tag (0x75)
      // Issuer info should be included in the name field instead
      // e.g., "Issuer:AccountName" format

      // Don't add length prefix - it will be added as Lc in APDU
      // payload.unshift(payload.length);
      
      console.log('OATH Add Payload:', byteToHexString(payload));
      console.log('Payload length:', payload.length, 'bytes');
      console.log('Key length:', keyArray.length);
      console.log('Name:', name);
      console.log('Name bytes length:', new TextEncoder().encode(name).length);
      console.log('Type:', type, 'Algo:', algo, 'Digits:', digits, 'Period:', period);

      const lcByte = payload.length.toString(16).padStart(2, '0');
      console.log('Lc byte:', lcByte, '(decimal:', payload.length, ')');
      const res = await dispatch(transceive(`00010000${lcByte}${byteToHexString(payload)}`));
      if (res.endsWith("9000")) {
        enqueueSnackbar('Add OATH credential success', {variant: 'success'});
        resetAddForm();
        setAddDialogOpen(false);
      } else {
        const statusCode = res.slice(-4);
        enqueueSnackbar(`Add OATH credential failed. Status: ${statusCode}`, {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
    await refresh();
    resetAddForm();
  }, [refresh, dispatch, name, algo, enqueueSnackbar, onlyIncreasing, requireTouch, selectOathApplet, type, keyArray, digits, period, counter, resetAddForm]);

  const doSetDefault = useCallback(async (name) => {
    try {
      // This function is deprecated. Use PASS page to configure OATH credentials for buttons.
      enqueueSnackbar(
        'Please use the PASS page to configure OATH credentials as button defaults. ' +
        'This function requires selecting a slot (short/long press) and only supports HOTP credentials.',
        {variant: 'info', autoHideDuration: 8000}
      );
      
      // Old implementation (kept for reference):
      // await selectOathApplet();
      // let data = [];
      // // name
      // data.push(0x71);
      // let nameArray = new TextEncoder().encode(name);
      // data.push(nameArray.length);
      // data.push(...nameArray);
      // // lc
      // data.unshift(data.length);
      // 
      // // Note: P1 should be 01 (short) or 02 (long), not 00
      // // Note: P2 should be 00 (no enter) or 01 (with enter)
      // let res = await dispatch(transceive(`00550000${byteToHexString(data)}`));
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [enqueueSnackbar]);

  const doCalculate = useCallback(async (name) => {
    try {
      // 确保 OATH applet 被选中（防止被其他操作如 Admin 认证检查踢出）
      await selectOathApplet();
      
      let data = [];
      // name
      data.push(0x71);
      let nameArray = new TextEncoder().encode(name);
      data.push(nameArray.length);
      data.push(...nameArray);
      // challenge
      data.push(0x74);
      let epoch = Math.round(new Date().getTime() / 1000.0);
      let challenge = Math.floor(epoch / 30.0); // 30s
      
      // Debug logging
      console.log('=== TOTP Debug Info ===');
      console.log('Current time:', new Date().toString());
      console.log('Epoch (seconds):', epoch);
      console.log('Challenge (epoch/30):', challenge);
      console.log('Challenge hex:', challenge.toString(16).padStart(16, '0'));
      console.log('========================');
      
      // 64bit
      let str = challenge.toString(16).padStart(16, '0');
      let challengeArray = hexStringToByte(str);
      data.push(challengeArray.length);
      data.push(...challengeArray);
      // lc
      data.unshift(data.length);

      let cmd = isOldProtocol(firmwareVersion) ? '04' : 'A2';
      let apduCommand = `00${cmd}0001${byteToHexString(data)}`;
      console.log('Sending APDU:', apduCommand);
      
      // P2=01 means truncated response (TOTP value), P2=00 means full response
      let res = await dispatch(transceive(apduCommand));
      
      console.log('=== TOTP Response Debug ===');
      console.log('Full response:', res);
      console.log('Response length:', res.length);
      
      if (res.endsWith("9000")) {
        // Parse truncated response: TAG(1) + LENGTH(1) + DIGITS(1) + VALUE(4)
        console.log('Response structure:');
        console.log('  TAG:', res.substr(0, 2));
        console.log('  LENGTH:', res.substr(2, 2));
        console.log('  DIGITS:', res.substr(4, 2));
        console.log('  VALUE (hex):', res.substr(6, 8));
        console.log('  Status:', res.substr(14, 4));
        
        let arr = hexStringToByte(res.substr(6, 8));
        console.log('  VALUE (bytes):', Array.from(arr).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
        
        let num = new DataView(arr.buffer).getUint32(0, false);
        console.log('  VALUE (uint32):', num);
        console.log('  VALUE (uint32, hex):', '0x' + num.toString(16).padStart(8, '0'));
        
        let totp_value = num % 1000000;
        console.log('  VALUE % 1000000:', totp_value);
        
        let totp = totp_value.toString().padStart(6, '0');
        console.log('  TOTP (final):', totp);
        console.log('===========================');
        
        let action = key => (
          <Button onClick={() => {
            navigator.clipboard.writeText(totp)
          }}>
            COPY
          </Button>
        );
        enqueueSnackbar(`TOTP code is ${totp}`, {variant: 'success', action});
      } else {
        enqueueSnackbar('Calculate TOTP failed', {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, enqueueSnackbar]); // 移除 firmwareVersion 依赖，避免状态变化导致的重复执行

  const openDeleteDialog = useCallback((name) => {
    setDeleteTargetName(name);
    setDeleteDialogOpen(true);
  }, []);

  const doDelete = useCallback(async () => {
    try {
      await selectOathApplet();
      let data = [];
      // name
      data.push(0x71);
      let nameArray = new TextEncoder().encode(deleteTargetName);
      data.push(nameArray.length);
      data.push(...nameArray);
      // lc
      data.unshift(data.length);

      let res = await dispatch(transceive(`00020000${byteToHexString(data)}`));
      if (res.endsWith("9000")) {
        enqueueSnackbar('Delete OATH credential success', {variant: 'success'});
        setDeleteDialogOpen(false);
        await refresh();
      } else {
        enqueueSnackbar('Delete OATH credential failed', {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [deleteTargetName, refresh, dispatch, enqueueSnackbar, selectOathApplet]);

  const openRenameDialog = useCallback((name) => {
    setRenameOldName(name);
    setRenameNewName(name);
    setRenameDialogOpen(true);
  }, []);

  const doRename = useCallback(async () => {
    try {
      if (!renameNewName || renameNewName.trim() === '') {
        enqueueSnackbar('New name cannot be empty', {variant: 'error'});
        return;
      }
      
      await selectOathApplet();
      let data = [];
      
      // old name
      data.push(0x71);
      let oldNameArray = new TextEncoder().encode(renameOldName);
      data.push(oldNameArray.length);
      data.push(...oldNameArray);
      
      // new name
      data.push(0x71);
      let newNameArray = new TextEncoder().encode(renameNewName);
      data.push(newNameArray.length);
      data.push(...newNameArray);
      
      // lc
      data.unshift(data.length);

      let res = await dispatch(transceive(`00050000${byteToHexString(data)}`));
      if (res.endsWith("9000")) {
        enqueueSnackbar('Rename OATH credential success', {variant: 'success'});
        setRenameDialogOpen(false);
        await refresh();
      } else {
        enqueueSnackbar('Rename OATH credential failed', {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [renameOldName, renameNewName, refresh, dispatch, enqueueSnackbar, selectOathApplet]);

  // QR Code Scanning Functions
  const stopQrScan = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (qrScanIntervalRef.current) {
      clearInterval(qrScanIntervalRef.current);
      qrScanIntervalRef.current = null;
    }
    setQrDialogOpen(false);
    setQrActive(false);
    setQrError('');
  }, []);

  const scanQrCode = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !qrActive) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      
      if (code) {
        try {
          const credential = parseOtpauthUri(code.data);
          
          // Fill in the add form
          setName(credential.name);
          setKey(credential.secret);
          setKeyEncoding('Base32');
          setAlgo(credential.algorithm === 'SHA256' ? 'HMAC-SHA256' : 'HMAC-SHA1');
          setType(credential.type);
          setDigits(credential.digits);
          setPeriod(credential.period);
          setCounter(credential.counter);
          setRequireTouch(credential.requireTouch);
          setOnlyIncreasing(credential.onlyIncreasing);
          
          // Close QR dialog and open add dialog
          stopQrScan();
          setAddDialogOpen(true);
          
          enqueueSnackbar('QR code scanned successfully!', { variant: 'success' });
        } catch (err) {
          setQrError(`Invalid QR code: ${err.message}`);
        }
      }
    }
  }, [qrActive, enqueueSnackbar, stopQrScan]);

  const startQrScan = useCallback(async () => {
    setQrDialogOpen(true);
    setQrError('');
    setQrActive(false);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setQrActive(true);
        
        // Start scanning
        qrScanIntervalRef.current = setInterval(() => {
          scanQrCode();
        }, 500);
      }
    } catch (err) {
      setQrError(`Failed to access camera: ${err.message}`);
    }
  }, [scanQrCode]);

  // Import/Export Functions
  const handleImportFile = useCallback(async (event) => {
    setImportError('');
    
    try {
      let data = importData;
      
      // If triggered by file input
      if (event && event.target && event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        data = await file.text();
        setImportData(data);
      }
      
      if (!data || !data.trim()) {
        setImportError('Please provide JSON data');
        return;
      }
      
      const credentials = JSON.parse(data);
      
      if (!Array.isArray(credentials)) {
        setImportError('JSON data must be an array');
        return;
      }
      
      await selectOathApplet();
      
      let successCount = 0;
      let failCount = 0;
      
      for (const raw of credentials) {
        try {
          const cred = normalizeImportedCredential(raw);
          
          // Build APDU payload
          const payload = [];
          const nameBytes = new TextEncoder().encode(cred.name);
          payload.push(0x71, nameBytes.length, ...nameBytes);
          
          const keyPayload = [];
          let flag = 0;
          if (cred.type === 'HOTP') {
            flag |= 0x10;
          } else if (cred.type === 'TOTP') {
            flag |= 0x20;
          }
          if (cred.algorithm === 'HMAC-SHA1' || cred.algorithm === 'SHA1') {
            flag |= 0x01;
          } else if (cred.algorithm === 'HMAC-SHA256' || cred.algorithm === 'SHA256') {
            flag |= 0x02;
          }
          
          keyPayload.push(flag);
          keyPayload.push(cred.digits);
          
          if (cred.type === 'HOTP') {
            const counterBuffer = new ArrayBuffer(8);
            const counterView = new DataView(counterBuffer);
            counterView.setBigUint64(0, BigInt(cred.counter), false); // eslint-disable-line no-undef
            keyPayload.push(...new Uint8Array(counterBuffer));
          } else {
            keyPayload.push(cred.period & 0xFF);
          }
          
          const secretBytes = buildKeyArrayFromSecret(cred.secret, 'Base32');
          keyPayload.push(...secretBytes);
          
          payload.push(0x73, keyPayload.length, ...keyPayload);
          payload.push(0x78, (cred.onlyIncreasing ? 0x01 : 0x00) | (cred.requireTouch ? 0x02 : 0x00));
          
          // Note: Device does NOT support issuer tag (0x75)
          // Issuer info should be included in the name field instead
          
          payload.unshift(payload.length);
          
          const res = await dispatch(transceive(`00010000${byteToHexString(payload)}`, false, true));
          if (res.endsWith("9000")) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }
      
      await refresh();
      
      enqueueSnackbar(`Import complete: ${successCount} succeeded, ${failCount} failed`, {
        variant: failCount > 0 ? 'warning' : 'success'
      });
      
      setImportDialogOpen(false);
      setImportData('');
    } catch (err) {
      setImportError(`Import failed: ${err.message}`);
    }
  }, [importData, dispatch, enqueueSnackbar, refresh, selectOathApplet]);

  const handleExportAll = useCallback(async () => {
    setExportError('');
    
    try {
      await selectOathApplet();
      
      const exportCreds = entries.map(entry => {
        const details = credentialStore[entry.name] || {};
        return {
          name: entry.name,
          secret: details.secret || '',
          type: entry.type,
          algorithm: entry.algo,
          digits: entry.digits || 6,
          period: entry.period || 30,
          counter: entry.counter || 0,
          issuer: entry.issuer || '',
          requireTouch: entry.requireTouch || false,
          onlyIncreasing: entry.onlyIncreasing || false
        };
      });
      
      const json = JSON.stringify(exportCreds, null, 2);
      setExportData(json);
      setExportDialogOpen(true);
      
      enqueueSnackbar(`Exported ${exportCreds.length} credentials`, { variant: 'success' });
    } catch (err) {
      setExportError(`Export failed: ${err.message}`);
      enqueueSnackbar(`Export failed: ${err.message}`, { variant: 'error' });
    }
  }, [entries, credentialStore, enqueueSnackbar, selectOathApplet]);

  const importFromClipboard = useCallback(async () => {
    try {
      let url = new URL(await navigator.clipboard.readText());
      if (url.protocol !== 'otpauth:') {
        throw 'Clipboard text is not a otpauth link';
      }

      if (url.searchParams.get("algorithm") === 'SHA1') {
        setAlgo("HMAC-SHA1");
      } else if (url.searchParams.get("algorithm") === 'SHA256') {
        setAlgo("HMAC-SHA256");
      } else if (url.searchParams.get("algorithm") !== null) {
        throw 'Unsupported algorithm';
      }

      if (url.pathname.startsWith("//totp")) {
        setType("TOTP");
      } else if (url.pathname.startsWith("//hotp")) {
        setType("HOTP");
      } else {
        throw 'Unsupproted type';
      }
      setName(url.pathname.substring(7));

      setKey(url.searchParams.get("secret"));
      setKeyEncoding("Base32");
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [enqueueSnackbar]);

  return (
    <div className={classes.root}>
      <Grid container spacing={1} justifyContent={"center"} className={classes.grid}>
        <Grid item xs={10}>
          <Card>
            <CardContent>
              <Typography variant="h3">
                OATH Applet
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Manage TOTP (Time-based) and HOTP (Counter-based) one-time password credentials for two-factor authentication.
              </Typography>
              
              {/* Admin 认证等待提示 */}
              {device && !adminAuthenticated && (
                <Box mt={2} mb={2} p={2} bgcolor="warning.light" borderRadius={4}>
                  <Typography variant="body1" color="textSecondary">
                    ⏳ Waiting for Admin authentication...
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Please enter your Admin PIN to view OATH credentials.
                  </Typography>
                </Box>
              )}
              
              {/* 账户列表 */}
              {device && adminAuthenticated && (
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Name
                        </TableCell>
                        <TableCell>
                          Type
                        </TableCell>
                        <TableCell>
                          Algorithm
                        </TableCell>
                        <TableCell>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.map((entry) => (
                      <TableRow key={entry.name}>
                        <TableCell>
                          {entry.name}
                        </TableCell>
                        <TableCell>
                          {entry.type}
                        </TableCell>
                        <TableCell>
                          {entry.algo}
                        </TableCell>
                        <TableCell>
                          {
                            entry.type === 'HOTP' ?
                              <Tooltip title="Set as default">
                                <IconButton onClick={() => doSetDefault(entry.name)}>
                                  <StarIcon/>
                                </IconButton>
                              </Tooltip>
                              :
                              <Tooltip title="Calculate TOTP">
                                <IconButton onClick={() => doCalculate(entry.name)}>
                                  <AvTimerIcon/>
                                </IconButton>
                              </Tooltip>
                          }
                          <Tooltip title="Rename">
                            <IconButton onClick={() => openRenameDialog(entry.name)}>
                              <EditIcon/>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => openDeleteDialog(entry.name)}>
                              <DeleteForeverIcon/>
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {entries.length === 0 ?
                      <TableRow>
                        <TableCell rowSpan={4}>
                          {device !== null ? 'No entries' : 'Please connect to device first'}
                        </TableCell>
                      </TableRow>
                      : null
                    }
                  </TableBody>
                </Table>
              </TableContainer>
              )}
              
              {device !== null && (
                <Box mt={2}>
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AddIcon/>}
                    onClick={() => setAddDialogOpen(true)}
                    style={{marginRight: 8}}
                  >
                    Add
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<CropFreeIcon/>}
                    onClick={startQrScan}
                    style={{marginRight: 8}}
                  >
                    Scan QR
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<PublishIcon/>}
                    onClick={() => setImportDialogOpen(true)}
                    style={{marginRight: 8}}
                  >
                    Import
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<GetAppIcon/>}
                    onClick={handleExportAll}
                  >
                    Export All
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Add credential to OATH Applet</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Set OATH credential info below or upload a QR code image
          </DialogContentText>
          
          {/* 二维码图片上传按钮 */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              ref={qrImageInputRef}
              type="file"
              accept="image/*"
              className={classes.hiddenInput}
              onChange={handleQrImageUpload}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CropFreeIcon />}
              onClick={() => qrImageInputRef.current?.click()}
              fullWidth
            >
              📷 Upload QR Code Image
            </Button>
          </Box>
          
          {/* 错误提示 */}
          {qrImageError && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant="caption" color="error">
                ⚠️ {qrImageError}
              </Typography>
            </Box>
          )}
          
          <FormGroup>
            <TextField
              fullWidth
              label="Name"
              value={name}
              error={name.length === 0}
              helperText={name.length > 0 ? 'Account name (e.g., Google - user@example.com)' : 'name cannot be empty'}
              onChange={(e) => setName(e.target.value)}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Key (Secret)"
              value={key}
              helperText="Secret key from the service provider"
              onChange={(e) => setKey(e.target.value)}
              margin="dense"
            />

            <FormControl>
              <InputLabel id="encoding-label">Key Encoding</InputLabel>
              <Select labelId="encoding-label" value={keyEncoding} onChange={(e) => setKeyEncoding(e.target.value)}>
                <MenuItem value="UTF-8">UTF-8</MenuItem>
                <MenuItem value="HEX">HEX</MenuItem>
                <MenuItem value="Base32">Base32</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="algo-label">Algorithm</InputLabel>
              <Select labelId="algo-label" value={algo} onChange={(e) => setAlgo(e.target.value)}>
                <MenuItem value={"HMAC-SHA1"}>HMAC-SHA1</MenuItem>
                <MenuItem value={"HMAC-SHA256"}>HMAC-SHA256</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="type-label">Type</InputLabel>
              <Select labelId="type-label" value={type} onChange={(e) => setType(e.target.value)}>
                <MenuItem value={"HOTP"}>HOTP</MenuItem>
                <MenuItem value={"TOTP"}>TOTP</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyIncreasing}
                  onChange={(e) => setOnlyIncreasing(e.target.checked)}
                />
              }
              label="Only increasing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={requireTouch}
                  onChange={(e) => setRequireTouch(e.target.checked)}
                />
              }
              label="Require touch"
            />
          </FormGroup>
          <Typography className={classes.hexPreview}>
            Key in hex: {byteToHexString(keyArray)}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button variant="contained" color="secondary" onClick={importFromClipboard}>
            Import Otpauth from Clipboard
          </Button>
          <Button variant="contained" color="primary" onClick={doAdd}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={passphraseDialogOpen} onClose={() => setPassphraseDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>OATH Authentication Required</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your OATH applet has password protection enabled. 
            Enter your OATH passphrase below to continue.
          </DialogContentText>
          <DialogContentText style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
            💡 <strong>Tip:</strong> With unified authentication enabled, you can use Admin PIN to access OATH 
            without needing a separate password. Go to <strong>Admin → OATH Security Settings → Clear OATH Password</strong> 
            to simplify your authentication experience.
          </DialogContentText>
          <TextField
            type="password"
            autoFocus
            fullWidth
            label="OATH Passphrase"
            onKeyPress={onKeyPress}
            onChange={(e) => setPassphrase(e.target.value)}
            style={{ marginTop: '15px' }}/>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassphraseDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={doAuthenticate}>
            Authenticate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the credential "{deleteTargetName}"?
            <br />
            <strong>This action cannot be undone!</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={doDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} fullScreen={fullScreen}>
        <DialogTitle>Rename OATH Credential</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a new name for the credential "{renameOldName}"
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            type="text"
            fullWidth
            value={renameNewName}
            onChange={(e) => setRenameNewName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                doRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={doRename} color="primary">
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Scanning Dialog */}
      <Dialog open={qrDialogOpen} onClose={stopQrScan} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Scan QR Code</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <video ref={videoRef} style={{ width: '100%', maxWidth: '500px', display: qrActive ? 'block' : 'none' }} />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {qrError && (
              <Typography color="error" style={{ marginTop: 16 }}>
                {qrError}
              </Typography>
            )}
            {!qrActive && !qrError && (
              <Typography>Initializing camera...</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={stopQrScan} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Import OATH Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Paste JSON data or upload a JSON file containing OATH credentials.
          </DialogContentText>
          <TextField
            multiline
            rows={10}
            fullWidth
            variant="outlined"
            value={importData}
            onChange={(e) => setImportData(e.target.value)}
            placeholder='[{"name":"GitHub","secret":"JBSWY3DPEHPK3PXP","type":"TOTP","algorithm":"SHA1","digits":6,"period":30}]'
            style={{ marginTop: 16, marginBottom: 16 }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current.click()}
            startIcon={<CloudDownloadIcon />}
          >
            Choose File
          </Button>
          {importError && (
            <Typography color="error" style={{ marginTop: 16 }}>
              {importError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleImportFile} color="primary" variant="contained">
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Export OATH Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Copy the JSON data below or download it as a file.
          </DialogContentText>
          <TextField
            multiline
            rows={15}
            fullWidth
            variant="outlined"
            value={exportData}
            InputProps={{ readOnly: true }}
            style={{ marginTop: 16, marginBottom: 16 }}
          />
          {exportError && (
            <Typography color="error" style={{ marginTop: 16 }}>
              {exportError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="secondary">
            Close
          </Button>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(exportData);
              enqueueSnackbar('Copied to clipboard!', { variant: 'success' });
            }}
            color="primary"
            startIcon={<FileCopyIcon />}
          >
            Copy
          </Button>
          <Button
            onClick={() => {
              const blob = new Blob([exportData], { type: 'application/json' });
              saveAs(blob, 'oath-credentials.json');
              enqueueSnackbar('Downloaded!', { variant: 'success' });
            }}
            color="primary"
            variant="contained"
            startIcon={<GetAppIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
